import type { CSSProperties, FC, FormEvent, ReactNode } from 'react'

import { useRouter } from 'next/router'
import { useState } from 'react'
import { Search } from 'lucide-react'

import { cn } from '@/lib/utils'

interface HeroSearchProps {
    className?: string
    style?: CSSProperties
}

/**
 * The marketplace search that anchors the bottom of the hero. Submitting
 * hands off to `/browse`, the same query the browse page already validates,
 * so an empty field simply opens the full catalogue.
 */
export const HeroSearch: FC<HeroSearchProps> = ({ className, style }): ReactNode => {
    const router = useRouter()
    const [term, setTerm] = useState('')

    const submit = (event: FormEvent<HTMLFormElement>): void => {
        event.preventDefault()

        const clean = term.trim()

        void router.push(clean ? `/browse?q=${encodeURIComponent(clean)}` : '/browse')
    }

    return (
        <form
            role='search'
            onSubmit={submit}
            style={style}
            className={cn(
                'flex h-[62px] items-stretch rounded-mta-sm border-2 border-ink-900 bg-paper shadow-offset-sm sm:h-[74px]',
                className
            )}
        >
            <label htmlFor='hero-search' className='sr-only'>
                Search the marketplace
            </label>

            <Search className='ml-4 size-5 shrink-0 self-center text-ink-900 sm:ml-6 sm:size-6' aria-hidden='true' />

            <input
                id='hero-search'
                type='search'
                value={term}
                onChange={(event) => setTerm(event.target.value)}
                placeholder='Find usernames, accounts, channels, electronics & more...'
                className='mta-focus min-w-0 flex-1 bg-transparent px-4 text-[15px] text-ink-900 outline-none placeholder:text-text-muted sm:px-5 sm:text-[17px]'
            />

            <button
                type='submit'
                className='mta-focus hero-label inline-flex shrink-0 items-center justify-center rounded-r-[6px] border-l-2 border-ink-900 bg-brand-red-ink px-6 text-[15px] tracking-[0.04em] text-white transition-colors duration-200 ease-mta hover:bg-ink-900 sm:px-14 sm:text-[19px]'
            >
                Search
            </button>
        </form>
    )
}
